/**
 * Shared tunables + physics for pickups and vehicles. The vehicle step is a
 * pure function run on the server (authority) and mirrored on the client for
 * the local driver so control feels responsive.
 */
import { boxToAabb, staticBoxOverlap, pushDyn } from "./movement.js";
import { Buttons } from "./protocol.js";
import { stepArenaKartDrive, arenaKartAfterMove } from "./arenaKart.js";
/** How long after a vehicle is destroyed before it respawns at its original
 *  spawn point (every map). */
export const VEHICLE_RESPAWN_MS = 25000;
export const PICKUPS = {
    health: { amount: 45, color: "#37d67a" },
    armor: { amount: 50, color: "#3fa9f5" },
    grenade: { amount: 2, color: "#8faa4a" },
    // timed power-ups (durations in ms)
    speed: { ms: 8000, color: "#ffd166" }, // OVERDRIVE — 1.45× move speed
    dmg: { ms: 10000, color: "#ff4d6d" }, // OVERCHARGE — double damage
    cloak: { ms: 8000, color: "#b39dff" }, // GHOST — near-invisible to others
    antigrav: { ms: 15000, gravityMul: -1.05, color: "#c17bff", spawnDelayMs: 120_000 }, // ANTIGRAV — fast upward pull, ONE per map, appears 2min in, never respawns
    radius: 1.8,
    respawnMs: 18000,
    bob: 0.25,
};
export const MAX_ARMOR = 100;
export const PICKUP_INFO = {
    health: {
        name: "Health Shot",
        effect: `+${PICKUPS.health.amount} HP`,
        detail: "Stored for later. Press 1 or Arrow Up to heal.",
        action: "inventory",
        keyHint: "Walk over",
        voice: "health",
    },
    armor: {
        name: "Armor Vest",
        effect: `+${PICKUPS.armor.amount} armor`,
        detail: `Applied now, up to ${MAX_ARMOR}.`,
        action: "instant",
        keyHint: "Walk over",
        voice: "armor",
    },
    grenade: {
        name: "Grenades",
        effect: `+${PICKUPS.grenade.amount} grenades`,
        detail: "Stored for later. Press 2 or E to throw.",
        action: "inventory",
        keyHint: "Walk over",
        voice: "grenade",
    },
    speed: {
        name: "Overdrive",
        effect: `1.45x move speed / ${Math.round(PICKUPS.speed.ms / 1000)}s`,
        detail: "Consumed immediately.",
        action: "instant",
        keyHint: "Walk over",
        voice: "overdrive",
    },
    dmg: {
        name: "Overcharge",
        effect: `2x damage / ${Math.round(PICKUPS.dmg.ms / 1000)}s`,
        detail: "Consumed immediately.",
        action: "instant",
        keyHint: "Walk over",
        voice: "overcharge",
    },
    cloak: {
        name: "Ghost Cloak",
        effect: `Near invisible / ${Math.round(PICKUPS.cloak.ms / 1000)}s`,
        detail: "Consumed immediately.",
        action: "instant",
        keyHint: "Walk over",
        voice: "ghost",
    },
    antigrav: {
        name: "Antigravity",
        effect: `Map-wide moon gravity / ${Math.round(PICKUPS.antigrav.ms / 1000)}s`,
        detail: "One-shot event for everyone.",
        action: "event",
        keyHint: "Walk over",
        voice: "antigravity",
    },
    jetpack: {
        name: "Jetpack",
        effect: "Air glide",
        detail: "Equipped permanently for this match.",
        action: "equip",
        keyHint: "Walk over",
        voice: "jetpack",
    },
    rocket: {
        name: "Rocket Launcher",
        effect: "3 rockets",
        detail: "Temporary heavy weapon ammo.",
        action: "equip",
        keyHint: "Walk over",
        voice: "rocketlauncher",
    },
    imperion: {
        name: "IMPERION",
        effect: "Charged beam rifle",
        detail: "Hold aim and fire to charge.",
        action: "equip",
        keyHint: "Walk over",
        voice: "imperion",
    },
    weaponchest: {
        name: "Weapon Chest",
        effect: "Random loaded weapon",
        detail: "Replaces your current weapon.",
        action: "equip",
        keyHint: "Walk over",
    },
    decoy: {
        name: "Body Double",
        effect: "Drops a holo-clone of you",
        detail: "It stands where you grabbed it — whoever shoots it is exposed on your minimap.",
        action: "instant",
        keyHint: "Walk over",
        voice: "body_double", // dedicated recorded line (was borrowing cloak's "ghost")
    },
    fartblaster: {
        name: "Fart Blaster",
        effect: "5 donut rounds — LETHAL touch",
        detail: "Fire throws a growing smoke ring. The smoke kills; the hole is safe.",
        action: "equip",
        keyHint: "Walk over",
        voice: "fartblaster",
    },
    beigechest: {
        name: "Penis Gun",
        effect: "Sperm hose — hold Fire to drain the reservoir",
        detail: "A continuous jet: everyone caught in the spray loses life. Ends when the reservoir empties.",
        action: "equip",
        keyHint: "Walk over",
        voice: "penisgun",
    },
    waterchest: {
        name: "Water Gun",
        effect: "Curving water hose — hold Fire to drain the tank",
        detail: "A continuous blue stream with a slight gravity arc. Ends when the tank empties.",
        action: "equip",
        keyHint: "Walk over",
        voice: "watergun",
    },
    music: {
        name: "Music Chest",
        effect: "Changes the track",
        detail: "Broadcast to everyone.",
        action: "event",
        keyHint: "Walk over",
        voice: "musicchange",
    },
    buy_ammo: {
        name: "SHOP",
        effect: "ammo · gear · upgrades",
        detail: "Buy ammo, gear and upgrades with points.",
        action: "shop",
        keyHint: "F / △",
    },
    buy_weapon: {
        name: "SHOP",
        effect: "ammo · gear · upgrades",
        detail: "Buy ammo, gear and upgrades with points.",
        action: "shop",
        keyHint: "F / △",
    },
    buy_perk: {
        name: "SHOP",
        effect: "ammo · gear · upgrades",
        detail: "Buy ammo, gear and upgrades with points.",
        action: "shop",
        keyHint: "F / △",
    },
    buy_mystery: {
        name: "SHOP",
        effect: "ammo · gear · upgrades",
        detail: "Buy ammo, gear and upgrades with points.",
        action: "shop",
        keyHint: "F / △",
    },
    buy_pap: {
        name: "SHOP",
        effect: "ammo · gear · upgrades",
        detail: "Buy ammo, gear and upgrades with points.",
        action: "shop",
        keyHint: "F / △",
    },
};
export const PURGE_SHOP = [
    { id: "grenades", icon: "🧨", name: "GRENADES ×2", desc: "Two frag grenades", cost: 250 },
    { id: "medkit", icon: "✚", name: "MEDKIT", desc: "+60 health now", cost: 300 },
    { id: "potion", icon: "🧪", name: "HEALING POTION", desc: "Stored heal — use with 1/↑", cost: 400 },
    { id: "ammo", icon: "🔋", name: "AMMO REFILL", desc: "Full magazine + 2 grenades", cost: 500 },
    { id: "armor", icon: "🛡", name: "ARMOR PLATE", desc: "+50 armor", cost: 750 },
    { id: "mystery", icon: "🎲", name: "MYSTERY WEAPON", desc: "Random weapon roll", cost: 950 },
    { id: "fartblaster", icon: "💨", name: "FART BLASTER", desc: "5 lethal donut rounds", cost: 1250 },
    { id: "weapon", icon: "🔫", name: "NEXT WEAPON", desc: "Upgrade to the next tier", cost: 1500 },
    { id: "life", icon: "💖", name: "EXTRA LIFE", desc: "+1 life (max 3)", cost: 2000 },
    { id: "pap", icon: "⚡", name: "PACK-A-PUNCH", desc: "1.9x damage for the whole run", cost: 5000 },
];
export const PURGE_PERKS = [
    { id: "vigor", icon: "❤️", name: "VIGOR", desc: "+30 max health (healed now)" },
    { id: "plating", icon: "🛡", name: "PLATING", desc: "+60 armor now, +40 every round" },
    { id: "fasthands", icon: "⚡", name: "FAST HANDS", desc: "Reload 25% faster" },
    { id: "juice", icon: "☠", name: "JUICED ROUNDS", desc: "+15% damage to the horde" },
    { id: "regen", icon: "💚", name: "REGENERATOR", desc: "Slowly regenerate health" },
    { id: "life", icon: "💖", name: "SECOND WIND", desc: "+1 life (up to 3)" },
    { id: "scavenger", icon: "💰", name: "SCAVENGER", desc: "+30% points earned" },
];
export function pickupInfo(kind) {
    // own-property check, not `??`: `PICKUP_INFO["toString"]` inherits a function
    // off Object.prototype, which is non-nullish and would defeat the fallback.
    if (Object.prototype.hasOwnProperty.call(PICKUP_INFO, kind))
        return PICKUP_INFO[kind];
    return {
        name: kind.replace(/_/g, " ").toUpperCase(),
        effect: "Bonus",
        detail: "Collect to use.",
        action: "instant",
        keyHint: "Walk over",
    };
}
/** Thrown grenade tuning (lobbed arc, fuse, area blast). Button E. */
export const GRENADE = {
    throwSpeed: 19,
    upBoost: 4, // adds arc so it lobs
    gravity: 22,
    fuseMs: 1500,
    half: 0.3,
    blastRadius: 6.5, // instant-kill radius
    blastDamage: 175,
    vehicleDamage: 220,
    maxCarry: 4,
    bounce: 0.35, // energy kept when it hits the world
};
/** ARENA (Bob-omb Blast) forward-bomb throw tuning. The kart's forward toss
 *  inherits the kart's momentum plus `impulse` m/s along the heading, so the
 *  bomb always lands a PREDICTABLE distance AHEAD of the kart regardless of how
 *  fast it is going (relative landing ≈ impulse × time-of-flight, which is
 *  independent of the kart's own speed). `vyBase` is the flat (aim 0) lob and
 *  `vyAim` extends it as the player holds pitch up (input.pitch, 0..0.9).
 *
 *  Tuned 2026-08-26: impulse was 30 (vy 10 + aim*26), which lobbed a flat throw
 *  ~30 m ahead — PAST every combat-range rival on the compact 1.5x arena, so you
 *  could never bomb a kart right in front of you (the core Bob-omb-Blast loop),
 *  and every close forward throw sailed over the target and wasted the bomb. The
 *  bot solver (solveKartBombLob) had to work around it; the human had NO short
 *  throw at all. Now a flat throw lands ~11 m ahead (a readable combat toss) and
 *  full pitch lobs to ~40 m (over cover at the pack) — the reachable band a
 *  battle arena actually fights in. See agent/bench-bot-kartbomb.mjs (the
 *  before/after landing geometry) and the throwBombForward / solveKartBombLob
 *  sites in GunGameRoom, which BOTH read this so they cannot drift (invariant 7).
 *  ARENA-ONLY: throwBombForward is called only from vehicleFire's arena branch. */
export const ARENA_BOMB_THROW = {
    impulse: 16, // m/s forward impulse added to the kart's momentum (was 30)
    vyBase: 6.5, // flat lob at aim 0 (was 10) — a short combat toss
    vyAim: 24, // extra vertical per unit aim, 0..0.9 (was 26) — hold pitch to lob far
    launchFwd: 2.2, // spawn offset out the nose (unchanged)
    launchUp: 0.7, // spawn height above the kart centre (unchanged)
};
/** ARENA (Bob-omb Blast) settled-mine live window, ms. When a thrown bomb first
 *  SETTLES on the ground it arms and re-times its fuse to `settleTime + this`
 *  (see GunGameRoom.stepGrenades). Shared so the client's mine telegraph (the
 *  accelerating blink/beep + danger ring in worldEntities.GrenadeMesh) can peak
 *  the warning at the REAL detonation of a late-arming lob instead of saturating
 *  against the fixed throw fuse. Server and client read this one constant so the
 *  telegraph and the authoritative pop cannot drift. ARENA-ONLY. */
export const ARENA_MINE_LIVE_MS = 1100;
/** Does a bot want to walk over this pickup? Shared by the in-combat
 *  "opportunistic chest detour" and the idle "loot run" so the two can never
 *  disagree about what's worth grabbing. `healthBelow` is the urgency knob (a
 *  bot mid-fight only breaks for health when badly hurt; an idle bot tops up
 *  sooner). `wantGrenade` is idle-only: a bot never abandons an engagement to
 *  fetch grenades, but will pick up a spare mag during a lull. The always-want
 *  buff items (speed / dmg / cloak) and decoys match the pre-existing detour
 *  list exactly; anything unlisted (buy stations, music, weaponchest — an
 *  objective, routed separately) returns false. */
export function botWantsPickup(kind, s, opts) {
    switch (kind) {
        case "jetpack": return !s.hasJetpack;
        case "rocket": return s.rocketAmmo === 0;
        case "fartblaster": return s.fartAmmo === 0;
        case "waterchest": return (s.waterFuel ?? 0) === 0;
        case "imperion": return s.imperionAmmo === 0;
        case "health": return s.hp < opts.healthBelow;
        case "armor": return s.armor === 0;
        case "grenade": return opts.wantGrenade && s.grenades < GRENADE.maxCarry;
        case "speed":
        case "dmg":
        case "cloak":
        case "decoy":
            return true;
        default: return false;
    }
}
/** Vehicle rocket tuning — fired by the kart/heli driver, explodes on impact. */
export const ROCKET = {
    speed: 60,
    gravity: 6,
    lifeMs: 3500,
    half: 0.4,
    blastRadius: 7.5,
    blastDamage: 100, // at the center; falls off to 0 at the edge
    vehicleDamage: 160,
    cooldownMs: 550, // snappy rocket cadence
};
/** Thrown ballistic-knife projectile tuning (final Gun Game tier). */
export const BALLISTIC = {
    speed: 32, // m/s launch — a short lob, not a rifle
    gravity: 26, // steep arc drop
    lifeMs: 900, // SMALL range: dies fast (~20 m before it drops out)
    half: 0.45, // hit radius
    spin: 30, // rad/s visual tumble
};
export const VEHICLES = {
    kart: {
        kind: "kart",
        displayName: "SCRAP KART",
        maxHp: 240,
        climbAnything: true, // rides up and over any obstacle — nothing stops it
        half: [0.8, 0.5, 1.25], // smaller = easier to place/thread through gaps
        eyeY: 1.05,
        muzzle: [0, 0.9, -1.5],
        color: "#b6432f",
        accent: "#f4c542",
    },
    horse: {
        kind: "kart",
        displayName: "HORSE",
        maxHp: 180,
        half: [0.55, 0.85, 1.25],
        eyeY: 1.8,
        muzzle: [0, 1.45, -0.8],
        color: "#6b3f24",
        accent: "#2f1a10",
        speedMul: 1.05,
        turnMul: 1.15,
        frictionMul: 1.25,
        jumpVel: 8.5,
    },
    camel: {
        kind: "kart",
        displayName: "CAMEL",
        maxHp: 210,
        half: [0.62, 0.95, 1.35],
        eyeY: 2.05,
        muzzle: [0, 1.65, -0.9],
        color: "#b98247",
        accent: "#5b351f",
        speedMul: 0.96,
        turnMul: 1.0,
        frictionMul: 1.35,
        jumpVel: 7.2,
    },
    // ARENA bomb-battle kart: smaller + rounder + brighter than the scrap kart,
    // obeys walls (no climbAnything). Drops collected bombs.
    bombkart: {
        kind: "kart",
        displayName: "BOMB KART",
        maxHp: 200,
        half: [0.46, 0.32, 0.68], // small footprint on the 2x-scaled arena map
        eyeY: 0.9,
        muzzle: [0, 0.6, -1.0],
        color: "#e5473a",
        accent: "#ffd23d",
        speedMul: 1.2, // zippy: time-to-contact is the fun (paired w/ 1.5x arenas)
        turnMul: 1.15,
        // R1/Space hop: MKDD's drift hop has no real rise (<2 u) — a 0.17 m bob
        jumpVel: 3.0,
        // MKDD Bob-omb Blast physics (arenaKart.ts). Replaces the old "comfort
        // kart" path (accelMul) — that code in stepVehicle is now unreachable.
        mkdd: true,
    },
    heli: {
        kind: "heli",
        displayName: "HIND GUNSHIP",
        maxHp: 340,
        half: [1.4, 1.0, 2.6],
        eyeY: 1.0,
        muzzle: [0, 0.2, -3.0],
        color: "#4a5340",
        accent: "#9fb04a",
    },
    // ---- Skyhaven exotics ----
    truck: {
        kind: "kart",
        displayName: "COLOSSUS TRUCK",
        maxHp: 600,
        half: [1.6, 1.3, 3.6], // a genuine road train
        eyeY: 2.6,
        muzzle: [0, 2.2, -3.8],
        color: "#7a1f14",
        accent: "#f4c542",
        speedMul: 0.9,
        turnMul: 0.55,
        ramDamage: 999, // driving over someone is INSTANT
    },
    miniufo: {
        kind: "heli",
        displayName: "SCOUT SAUCER",
        maxHp: 260,
        half: [1.9, 0.8, 1.9],
        eyeY: 1.25,
        muzzle: [0, 0.3, -2.2],
        color: "#8a9bb0",
        accent: "#00f0ff",
        speedMul: 1.25,
        turnMul: 1.4,
        lookFlight: true,
    },
    // ---- four extra rides scattered across the roomy maps ----
    speeder: {
        kind: "kart",
        displayName: "NEON SPEEDER",
        maxHp: 150,
        half: [0.55, 0.45, 1.15], // sleek hoverbike
        eyeY: 1.0,
        muzzle: [0, 0.7, -1.3],
        color: "#00e0ff",
        accent: "#ff2ec4",
        speedMul: 1.95, // FAST + darty
        turnMul: 1.7,
        jumpVel: 9, // little hops
    },
    artillery: {
        kind: "kart",
        displayName: "SIEGE CRAWLER",
        maxHp: 460,
        half: [1.3, 1.0, 2.2],
        eyeY: 2.2,
        muzzle: [0, 1.8, -2.6],
        color: "#5a5030",
        accent: "#ffcf3a",
        speedMul: 0.8,
        turnMul: 0.6,
        salvo: 3, // fans out a THREE-rocket bombardment per trigger pull
    },
    gunship: {
        kind: "heli",
        displayName: "REAPER GUNSHIP",
        maxHp: 420,
        half: [1.6, 1.1, 3.0],
        eyeY: 1.1,
        muzzle: [0, 0.2, -3.4],
        color: "#20242e",
        accent: "#ff5533",
        speedMul: 0.95,
        turnMul: 0.95,
        salvo: 2, // twin rocket pods
    },
    interceptor: {
        kind: "heli",
        displayName: "ARROW INTERCEPTOR",
        maxHp: 170,
        half: [1.5, 0.65, 1.7],
        eyeY: 0.9,
        muzzle: [0, 0.2, -2.2],
        color: "#e8edf5",
        accent: "#2a6bff",
        speedMul: 1.9, // fastest flyer
        turnMul: 1.25,
        plane: true,
    },
    miniplane: {
        kind: "heli",
        displayName: "SPARROW MINIPLANE",
        maxHp: 180,
        half: [1.6, 0.7, 1.6],
        eyeY: 0.9,
        muzzle: [0, 0.2, -2.0],
        color: "#c8d0dc",
        accent: "#ff5a2a",
        speedMul: 1.5, // fast little flyer
        turnMul: 1.2,
        plane: true, // flies like a plane, not a hovering heli
    },
    delorean: {
        kind: "kart",
        displayName: "DELOREAN TIME MACHINE",
        maxHp: 880,
        half: [1.05, 0.55, 2.05],
        eyeY: 1.15,
        muzzle: [0, 0.75, -2.0],
        color: "#9ea7ad",
        accent: "#28f4ff",
        speedMul: 0,
        turnMul: 0,
        frictionMul: 3,
        timeMachine: true,
    },
    // SUBMARINE: a heli-physics ride tuned for the SEA — it hovers (neutral
    // buoyancy), and since the water surface is non-solid it dives below with
    // Crouch and surfaces with Jump. Steady + torpedo-slow, look-steered.
    submarine: {
        kind: "heli",
        displayName: "NAUTILUS SUB",
        maxHp: 420,
        half: [1.5, 1.2, 3.4],
        eyeY: 1.3,
        muzzle: [0, 0.2, -3.6],
        color: "#3a4d3f",
        accent: "#d8b34a",
        speedMul: 0.8,
        turnMul: 0.9,
        lookFlight: true, // swim in the look direction (aim down to descend)
        submarine: true, // clamped under the surface; torpedoes fire dead ahead
    },
    // SHIP CANNON: bolted to the deck. Mount it (Use), aim with your look, FIRE
    // arcing cannonballs — ship-to-ship bombardment + shelling swimmers. speedMul
    // 0 keeps it put (same trick as the DeLorean).
    cannon: {
        kind: "kart",
        displayName: "SHIP CANNON",
        maxHp: 600,
        half: [0.85, 0.55, 1.5],
        eyeY: 0.95,
        muzzle: [0, 0.55, -1.8],
        color: "#26262b",
        accent: "#c9a53a",
        speedMul: 0,
        turnMul: 0,
        frictionMul: 4,
        cannon: true,
    },
};
/** Skyhaven's big UFO when PILOTED: arcade saucer flight + the crash nuke.
 *  Board the deck, press Use near the dome to take the helm; any contact with
 *  the world (or the ground) detonates it — everyone in the blast is killed
 *  AND demOTED. The saucer respawns on its circuit later. */
export const UFO = {
    turnRate: 0.9, // rad/s yaw authority
    accel: 15, // m/s² forward thrust
    lift: 10, // m/s² climb/dive authority
    drag: 0.45, // horizontal drag
    maxSpeed: 26,
    sinkAdrift: 3.2, // (legacy) — adrift saucers now PLUMMET (see stepUfo)
    hullR: 15, // crash-test hull radius (slightly inside the 19m visual)
    hullDepth: 4, // crash-test hull depth below the deck
    crashRadius: 34, // lethal blast radius
    groundY: 3, // deck altitude at/below which it counts as ground impact
    respawnMs: 30000,
};
// ---- rooftop cable platforms (Skyhaven): gondolas gliding between the
// tallest towers. Pure functions of map + time so client and server agree.
export const CABLE = { period: 18, half: 1.7, thick: 0.4, dwell: 2 / 18 }; // 2 s parked at each end
export const ELEVATOR = { period: 22, half: 1.3, thick: 0.35, dwell: 1 / 22, solid: 2.4 };
export const PRACTICE_MOVER_TAG = "practice_mover";
/** Floating Practice Field islands. Group the visible top slab with its underside
 *  and send each island on a wide, fast route across the practice dimension. */
export function practiceMovers(map) {
    if (map.id !== "sandlot")
        return [];
    const tagged = map.boxes.filter((b) => b.tag === PRACTICE_MOVER_TAG);
    if (!tagged.length)
        return [];
    const grouped = new Map();
    for (const b of tagged) {
        const key = b.moveGroup ?? `${Math.round(b.p[0] * 10) / 10}|${Math.round(b.p[2] * 10) / 10}`;
        (grouped.get(key) ?? grouped.set(key, []).get(key)).push(b);
    }
    const groupCenterZ = (parts) => parts.reduce((sum, b) => sum + b.p[2], 0) / Math.max(1, parts.length);
    return [...grouped.entries()]
        .sort((a, b) => groupCenterZ(a[1]) - groupCenterZ(b[1]))
        .map(([group, parts], i) => {
        const top = parts.reduce((best, b) => b.p[1] + b.s[1] / 2 > best.p[1] + best.s[1] / 2 ? b : best, parts[0]);
        const base = [top.p[0], top.p[1], top.p[2]];
        const tower = group === "practice_sky_tower";
        return {
            group,
            base,
            parts: parts.map((b) => ({
                offset: [b.p[0] - base[0], b.p[1] - base[1], b.p[2] - base[2]],
                s: b.s,
                color: b.color,
                glow: b.glow,
            })),
            halfX: Math.max(...parts.map((b) => b.s[0] / 2)),
            halfZ: Math.max(...parts.map((b) => b.s[2] / 2)),
            topY: top.p[1] + top.s[1] / 2,
            // Each island gets its OWN speed + phase so they drift in DIFFERENT
            // directions (distinct elliptical orbits), faster than before. Safe from
            // collisions: islands close in XZ are always far apart in height (the
            // route is height-staggered 7-18 m, decks are thin), and amplitude stays
            // modest (~3.6, tower ~2.2) so far-apart islands never reach each other.
            speed: 0.55 + (i % 4) * 0.09,
            phase: i * 2.3,
            bobPhase: i * 1.7,
            driftPhase: i * 1.1 + 0.6,
            ampX: tower ? 2.2 : 3.6,
            ampY: tower ? 0.5 : 0.55 + ((i * 3) % 4) * 0.14,
            ampZ: tower ? 2.2 : 3.6,
        };
    });
}
export function practiceMoverPose(p, t) {
    return [
        p.base[0] + Math.sin(t * p.speed + p.phase) * p.ampX + Math.sin(t * p.speed * 0.37 + p.phase * 2.1) * p.ampX * 0.28,
        p.base[1] + Math.sin(t * p.speed * 1.7 + p.bobPhase) * p.ampY,
        p.base[2] + Math.cos(t * p.speed * 0.73 + p.driftPhase) * p.ampZ,
    ];
}
/** Moving-platform colliders for the Practice-Field islands.
 *
 *  Pass `dynamic` + `pool` to append allocation-free (the hot path — see
 *  `pushDyn`); omit both for a fresh array (tests / one-off callers). */
export function practiceMoverColliderBoxes(movers, t, prevT, dynamic, pool) {
    const out = dynamic ?? [];
    for (const p of movers) {
        const cur = practiceMoverPose(p, t);
        const prev = practiceMoverPose(p, prevT);
        const dx = cur[0] - prev[0], dy = cur[1] - prev[1], dz = cur[2] - prev[2];
        for (const b of p.parts) {
            // Every part is solid (walls included, so the flying tower can be entered
            // and its shaft climbed). Riders on the deck are carried by dx/dz, so a
            // wall moving in lock-step with the deck never shoves them.
            const cx = cur[0] + b.offset[0];
            const cy = cur[1] + b.offset[1];
            const cz = cur[2] + b.offset[2];
            const minX = cx - b.s[0] / 2, maxX = cx + b.s[0] / 2;
            const minY = cy - b.s[1] / 2, maxY = cy + b.s[1] / 2;
            const minZ = cz - b.s[2] / 2, maxZ = cz + b.s[2] / 2;
            if (dynamic && pool)
                pushDyn(dynamic, pool, minX, maxX, minY, maxY, minZ, maxZ, dx, dy, dz);
            else
                out.push({ minX, maxX, minY, maxY, minZ, maxZ, dx, dy, dz });
        }
    }
    return out;
}
/** Re-pin any jump pad resting on a moving Practice-Field island to that
 *  island's current pose, so a grav-lift beam stays centred inside its tower as
 *  the tower drifts. Deterministic from the shared cable clock (same `t` on the
 *  server and in client prediction), so it never causes a reconcile fight. */
export function syncMoverPads(world, movers, t) {
    if (!movers.length || !world.pads.length)
        return;
    for (let i = 0; i < world.pads.length; i++) {
        const home = world.padHome[i];
        for (const m of movers) {
            if (Math.abs(home.x - m.base[0]) > m.halfX + 2)
                continue;
            if (Math.abs(home.z - m.base[2]) > m.halfZ + 2)
                continue;
            const pose = practiceMoverPose(m, t);
            world.pads[i].x = pose[0] + (home.x - m.base[0]);
            world.pads[i].y = pose[1] + (home.y - m.base[1]);
            world.pads[i].z = pose[2] + (home.z - m.base[2]);
            break;
        }
    }
}
const SKYHAVEN_DISCO = {
    // High over the central skylight, above the towers and well below the map cap.
    base: [0, 66, 0],
    radius: 7.5,
    platformHalf: 6.8,
    platformDepth: 0.9,
};
export function skyhavenDiscoBall(map) {
    return map.id === "neo_island" ? SKYHAVEN_DISCO : null;
}
export function skyhavenDiscoPose(ball, t) {
    return {
        x: ball.base[0] + Math.sin(t * 0.18) * 17 + Math.sin(t * 0.07 + 1.9) * 5,
        y: ball.base[1] + Math.sin(t * 0.31 + 0.4) * 2.4,
        z: ball.base[2] + Math.cos(t * 0.15 + 0.7) * 15,
        yaw: t * 0.9,
        roll: Math.sin(t * 0.23) * 0.18,
    };
}
export function skyhavenDiscoColliderBoxes(ball, t, prevT, dynamic, pool) {
    const out = dynamic ?? [];
    if (!ball)
        return out;
    const cur = skyhavenDiscoPose(ball, t);
    const prev = skyhavenDiscoPose(ball, prevT);
    const topY = cur.y + ball.radius * 0.82;
    const minX = cur.x - ball.platformHalf, maxX = cur.x + ball.platformHalf;
    const minY = topY - ball.platformDepth, maxY = topY;
    const minZ = cur.z - ball.platformHalf, maxZ = cur.z + ball.platformHalf;
    const dx = cur.x - prev.x, dy = cur.y - prev.y, dz = cur.z - prev.z;
    if (dynamic && pool)
        pushDyn(dynamic, pool, minX, maxX, minY, maxY, minZ, maxZ, dx, dy, dz);
    else
        out.push({ minX, maxX, minY, maxY, minZ, maxZ, dx, dy, dz });
    return out;
}
/** The 4 tallest DISTINCT tower roofs on Skyhaven (towers are hollow wall
 *  shells, so we detect flat wide roof slabs and dedupe stacked layers). */
function cableTowers(map) {
    if (map.id !== "neo_island")
        return [];
    const cands = map.boxes
        .filter((b) => b.s[1] <= 2 && Math.min(b.s[0], b.s[2]) >= 6 && b.p[1] > 14)
        .sort((x, y) => y.p[1] + y.s[1] / 2 - (x.p[1] + x.s[1] / 2));
    const towers = [];
    for (const c of cands) {
        if (towers.every((t) => Math.hypot(t.p[0] - c.p[0], t.p[2] - c.p[2]) > 25))
            towers.push(c);
        if (towers.length === 4)
            break;
    }
    return towers;
}
export function cableRoutes(map) {
    const towers = cableTowers(map);
    const routes = [];
    for (let i = 0; i + 1 < towers.length; i++) {
        const t1 = towers[i], t2 = towers[i + 1];
        // park the gondola at each roof's EDGE facing the other tower (not the
        // middle of the roof): walk each endpoint out to its box boundary along
        // the route direction, keeping the platform fully over the roof.
        const dx = t2.p[0] - t1.p[0], dz = t2.p[2] - t1.p[2];
        const len = Math.hypot(dx, dz) || 1;
        const ux = dx / len, uz = dz / len;
        const edge = (t, sx, sz) => {
            // distance from roof centre to its boundary along (sx,sz)
            const tx = Math.abs(sx) > 1e-4 ? (t.s[0] / 2) / Math.abs(sx) : Infinity;
            const tz = Math.abs(sz) > 1e-4 ? (t.s[2] / 2) / Math.abs(sz) : Infinity;
            return Math.max(0, Math.min(tx, tz) - CABLE.half - 0.4);
        };
        const d1 = edge(t1, ux, uz), d2 = edge(t2, -ux, -uz);
        routes.push({
            a: [t1.p[0] + ux * d1, t1.p[1] + t1.s[1] / 2 + CABLE.thick, t1.p[2] + uz * d1],
            b: [t2.p[0] - ux * d2, t2.p[1] + t2.s[1] / 2 + CABLE.thick, t2.p[2] - uz * d2],
        });
    }
    return routes;
}
/** Smooth ping-pong along the route (eases at both endpoints); `dwell` is
 *  the FRACTION of the period spent parked at each end before reversing. */
export function cablePos(r, tSec, phase = 0, period = CABLE.period, dwell = 0) {
    let u = ((tSec / period) + phase) % 1;
    if (u < 0)
        u += 1;
    const travel = 0.5 - dwell;
    let k;
    if (u < dwell)
        k = 0;
    else if (u < 0.5)
        k = 0.5 - 0.5 * Math.cos(((u - dwell) / travel) * Math.PI);
    else if (u < 0.5 + dwell)
        k = 1;
    else
        k = 0.5 + 0.5 * Math.cos(((u - 0.5 - dwell) / travel) * Math.PI);
    return [r.a[0] + (r.b[0] - r.a[0]) * k, r.a[1] + (r.b[1] - r.a[1]) * k, r.a[2] + (r.b[2] - r.a[2]) * k];
}
/** Outside ELEVATORS: one per gondola tower, riding the facade from street
 *  level to the roof edge (offset just outside the roof footprint). */
export function elevatorRoutes(map) {
    return cableTowers(map).map((t) => {
        const z = t.p[2];
        // start just off the +x face and keep stepping OUT until the whole
        // column clears every tall box — the platform must ride the OUTSIDE
        // of the facade, never inside a wall or balcony
        let x = t.p[0] + t.s[0] / 2 + ELEVATOR.half + 0.4;
        const roofY = t.p[1] + t.s[1] / 2;
        const blocked = (xx) => map.boxes.some((b) => {
            const top = b.p[1] + b.s[1] / 2, bot = b.p[1] - b.s[1] / 2;
            // anything between street level and the roof counts — wider podium
            // floors and awnings too, not just full-height walls
            return top > 2.2 && bot < roofY - 1 &&
                Math.abs(xx - b.p[0]) < b.s[0] / 2 + ELEVATOR.half &&
                Math.abs(z - b.p[2]) < b.s[2] / 2 + ELEVATOR.half;
        });
        for (let i = 0; i < 24 && blocked(x); i++)
            x += 0.5;
        return { a: [x, 1.4, z], b: [x, roofY + ELEVATOR.thick, z] };
    });
}
// ---- kart physics ----
const KART = {
    accel: 26, // m/s^2
    maxSpeed: 19,
    reverseSpeed: 8,
    turnRate: 3.0, // rad/s at speed — nimbler now it's smaller
    friction: 3.5,
    gravity: 26,
    // ARENA-only steeper DESCENT gravity (see the gravity line in stepVehicle):
    // ~1.7x so a hop or pad/ramp launch snaps back down (fall ~0.17s from a hop
    // vs ~0.22s) instead of lingering. Rise is unchanged, so peak height is too.
    arenaFallGravity: 44,
    // ---- ARENA-ONLY mini-turbo (Mario-Kart drift boost) ----
    // A sustained drift charges a boost; releasing (or breaking) the drift spends
    // the charge as a forward speed burst. Two tiers, like MK's blue/orange
    // sparks. All of this is gated on the arena comfort kart (`comfortKart`) in
    // stepVehicle — non-arena karts never drift (drift is arena-only, see
    // vehicleControlFromInput) so their driftCharge/boostT stay 0 and their speed
    // path is byte-for-byte unchanged (invariant 7 / arena isolation). Stateless
    // over (driftCharge, boostT) — both live in VehicleKin and are replicated, so
    // the client's re-seed-then-replay prediction and the server tick agree
    // tick-for-tick. No Date.now / random. Verified in agent/bench-arena-miniturbo.mjs.
    mtChargeMax: 1.5, // s — cap so an endless drift can't over-charge
    mtT1: 0.5, // s of continuous drift → tier-1 boost
    mtT2: 1.15, // s → tier-2 boost
    mtBoost1: 0.6, // s of boost from tier 1
    mtBoost2: 1.15, // s of boost from tier 2
    // Per-tier top-speed multiplier while boosting. MK's orange (super) mini-turbo
    // is BOTH longer AND stronger than the blue one — tier-2 must out-punch tier-1,
    // not merely outlast it, or committing to the long drift buys nothing but a few
    // extra tenths at the same speed. tier-1: 19*1.2(sMul)=22.8 → ~33 m/s;
    // tier-2: → ~35.3 m/s. The multiplier that's in force is carried in `boostMul`
    // (replicated) so the client re-seed/replay agrees with the server (invariant 7).
    mtBoostMul1: 1.45, // tier-1 (blue) top-speed multiplier
    mtBoostMul2: 1.55, // tier-2 (orange) top-speed multiplier — stronger burst
    mtBoostRate: 5.5, // exp approach rate to the boosted cap — a snappy kick
    mtOverspeedDecay: 14, // m/s² — bleed the overspeed smoothly when boost ends
};
// ---- heli physics ----
const HELI = {
    lift: 30, // upward thrust when ascending
    gravity: 12,
    maxUp: 12,
    moveAccel: 20,
    maxSpeed: 26,
    turnRate: 1.8,
    drag: 1.4,
    hoverDamp: 2.5,
};
/** True for kinds the client may run local prediction on. The two exceptions
 *  are driven OUTSIDE the shared vehicle step: the DeLorean is locked in place
 *  by `driveTimeMachine`, and the ship cannon is bolted to a (possibly moving)
 *  deck — predicting either would fight the server's scripted pose. */
export function vehiclePredictable(cfg) {
    return !cfg.timeMachine && !cfg.cannon;
}
/** Resolve the config the SERVER simulates a driven vehicle with. Arena strips
 *  `climbAnything` (walls must block karts there). Cached per kind so per-input
 *  callers — the server's drive path and the client's replay — never allocate. */
const arenaCfgCache = new Map();
export function driveVehicleCfg(kind, arena) {
    const cfg = VEHICLES[kind] ?? VEHICLES.kart;
    if (!arena || !cfg.climbAnything)
        return cfg;
    let c = arenaCfgCache.get(kind);
    if (!c) {
        c = { ...cfg, climbAnything: false };
        arenaCfgCache.set(kind, c);
    }
    return c;
}
/** The EXACT control mapping the server applies to a driver's raw input,
 *  shared so the client's vehicle prediction replays it bit-identically
 *  (invariant 7 — any drift here renders as permanent correction jitter).
 *  `kinYaw` is the hull's CURRENT heading (ground rides look-steer toward the
 *  camera when the stick is centered). Pass `out` to avoid allocating in a
 *  per-input replay loop. */
export function vehicleControlFromInput(kind, arena, spinning, input, kinYaw, out) {
    // NEW terrestrial rides (speeder/artillery/mounts) steer TOWARD THE CAMERA
    // like the flying vehicles — drive where you look; pushing the stick
    // sideways still overrides with manual steering ("ground vehicles are hell")
    let steer = spinning ? 0 : Math.max(-1, Math.min(1, input.moveX));
    if (!spinning && (kind === "speeder" || kind === "artillery" || kind === "horse" || kind === "camel") && Math.abs(input.moveX) < 0.25) {
        let dyaw = input.yaw - kinYaw;
        while (dyaw > Math.PI)
            dyaw -= 2 * Math.PI;
        while (dyaw < -Math.PI)
            dyaw += 2 * Math.PI;
        steer = -Math.max(-1, Math.min(1, dyaw * 1.6)); // kart steer sign is negated
    }
    const c = out ?? {};
    c.throttle = spinning ? 0 : Math.max(-1, Math.min(1, input.moveZ));
    c.steer = steer;
    c.ascend = !spinning && (input.buttons & Buttons.Jump) !== 0;
    c.descend = !spinning && (input.buttons & Buttons.Crouch) !== 0;
    c.boost = !spinning && (input.buttons & Buttons.Sprint) !== 0; // L3/Ctrl — heli afterburner
    c.drift = !spinning && arena && (input.buttons & Buttons.FreeLook) !== 0;
    c.yaw = input.yaw;
    return c;
}
/** Post-step play-space clamp — keep vehicles inside the map. Helis have no
 *  built-in ceiling and could otherwise hover forever above/outside the arena,
 *  unreachable. BUT if the map has a flying platform (the pirate ghost ship),
 *  the ceiling must clear its PEAK altitude + margin so a heli can climb up
 *  and LAND on it. Shared verbatim between the server's drive path and the
 *  client's prediction replay. */
export function clampVehicleToMap(k, map) {
    const b = map.bounds;
    const planeTop = map.plane ? map.plane.baseY + map.plane.climbY + 10 : 0;
    const vehicleCeil = Math.max(b.max[1] + 6, planeTop);
    k.x = Math.max(b.min[0] - 2, Math.min(b.max[0] + 2, k.x));
    k.z = Math.max(b.min[2] - 2, Math.min(b.max[2] + 2, k.z));
    if (k.y > vehicleCeil) {
        k.y = vehicleCeil;
        k.vy = Math.min(0, k.vy);
    }
}
/** The EXACT control mapping the server applies to the worm pilot's raw input,
 *  shared so the client's worm prediction replays it bit-identically
 *  (invariant 7 — the driveVehicle treatment; edit HERE, never inline).
 *  Pass `out` to avoid allocating in a per-input replay loop. */
export function wormControlFromInput(input, out) {
    out.throttle = Math.max(-1, Math.min(1, input.moveZ));
    out.steer = Math.max(-1, Math.min(1, input.moveX));
    out.yaw = input.yaw;
    out.pitch = input.pitch;
    out.boost = (input.buttons & Buttons.Sprint) !== 0;
    out.fire = (input.buttons & Buttons.Fire) !== 0;
    return out;
}
/** Advance the PILOTED worm one input's worth of planar motion. Extracted
 *  verbatim from GunGameRoom.updateWorm's piloted branch so the server's
 *  per-input step and the pilot's client prediction cannot drift. Yaw is a
 *  direct mapping (camera yaw + steer lean), NOT integrated — only x/z carry
 *  state between steps. dt is clamped inside, stepVehicle-style, so a forged
 *  dtMs is bounded the same way vehicles bound it. Mutates `pose`. */
export function stepWormPiloted(pose, c, dtMs, bounds) {
    const dt = Math.min(0.05, Math.max(0, dtMs / 1000));
    const yaw = c.yaw + c.steer * 0.35;
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const speed = (c.boost ? 34 : 23) * c.throttle;
    pose.x = Math.max(bounds.min[0] + 8, Math.min(bounds.max[0] - 8, pose.x + fx * speed * dt));
    pose.z = Math.max(bounds.min[2] + 8, Math.min(bounds.max[2] - 8, pose.z + fz * speed * dt));
    pose.yaw = yaw;
}
/** The EXACT control mapping the server applies to the UFO pilot's raw input,
 *  shared so the pilot's client prediction integrates it identically
 *  (invariant 7 — the driveVehicle/worm treatment; edit HERE, never inline).
 *  Pass `out` to avoid allocating in a per-frame prediction loop. */
export function ufoControlFromInput(input, out) {
    out.steer = Math.max(-1, Math.min(1, input.moveX));
    out.throttle = Math.max(-1, Math.min(1, input.moveZ));
    out.ascend = (input.buttons & Buttons.Jump) !== 0;
    out.descend = (input.buttons & Buttons.Crouch) !== 0;
    out.yaw = input.yaw;
    return out;
}
/** Advance the PILOTED saucer one step of flight — the FULL integration the
 *  server runs for an alive pilot: heading is a direct mapping of the look yaw,
 *  thrust accelerates along heading + strafe, ascend/descend or hover-damp on
 *  Y, then horizontal drag, speed clamp, and position integrate. Extracted
 *  verbatim from GunGameRoom.stepUfo's alive-pilot branch + its common tail so
 *  the server's per-tick step and the pilot's client prediction cannot drift
 *  (the same reason stepWormPiloted exists). dt is clamped inside exactly as
 *  the server clamps it. Mutates `k`. Does NOT do collision/crash — that stays
 *  server-authoritative. */
export function stepUfoPiloted(k, c, dtMs) {
    const dt = Math.min(0.1, Math.max(0, dtMs / 1000));
    k.yaw = c.yaw;
    const fx = -Math.sin(k.yaw), fz = -Math.cos(k.yaw);
    const rx = Math.cos(k.yaw), rz = -Math.sin(k.yaw);
    k.vx += (fx * c.throttle + rx * c.steer) * UFO.accel * dt;
    k.vz += (fz * c.throttle + rz * c.steer) * UFO.accel * dt;
    if (c.ascend)
        k.vy += UFO.lift * dt;
    else if (c.descend)
        k.vy -= UFO.lift * dt;
    else
        k.vy -= k.vy * 2 * dt; // hover damp
    k.vx -= k.vx * UFO.drag * dt;
    k.vz -= k.vz * UFO.drag * dt;
    const hspSq = k.vx * k.vx + k.vz * k.vz;
    if (hspSq > UFO.maxSpeed * UFO.maxSpeed) {
        const hsp = Math.sqrt(hspSq);
        k.vx *= UFO.maxSpeed / hsp;
        k.vz *= UFO.maxSpeed / hsp;
    }
    k.x += k.vx * dt;
    k.y += k.vy * dt;
    k.z += k.vz * dt;
}
/** The EXACT control mapping the server applies to the dragon rider's raw
 *  input, shared so the rider's client prediction replays it bit-identically
 *  (invariant 7 — the worm/UFO treatment; edit HERE, never inline). Pass `out`
 *  to avoid allocating in a per-input replay loop. */
export function dragonControlFromInput(input, out) {
    out.throttle = Math.max(-1, Math.min(1, input.moveZ));
    out.yaw = input.yaw;
    out.pitch = input.pitch;
    out.ascend = (input.buttons & Buttons.Jump) !== 0;
    out.descend = (input.buttons & Buttons.Crouch) !== 0;
    out.boost = (input.buttons & Buttons.Sprint) !== 0;
    return out;
}
/** Advance the PILOTED dragon one input's worth of open-air flight — thrust
 *  straight along the look direction, ascend/descend on Y, then the map-bounds
 *  clamp. Extracted verbatim from GunGameRoom.updateDragon's ridden branch (its
 *  integration + clamp only) so the server's per-input step and the rider's
 *  client prediction cannot drift (the same reason stepWormPiloted exists). dt
 *  is clamped inside to MOVE.maxDtMs (0.05 s) — the SAME bound the server's
 *  per-input drain budget charges (GunGameRoom `Math.min(MOVE.maxDtMs, …,
 *  input.dtMs)`), so a forged dtMs cannot buy more flight than it pays for.
 *  (The old inline lived in updateDragon and clamped to the TICK dt's 0.1 s;
 *  moving it per-input meant that 0.1 s let a client sending dtMs=100 integrate
 *  ~2× the distance its 50 ms budget debit allowed — a dragon speed hack. Match
 *  the worm/vehicle 0.05 s bound.) Mutates `pose`. Does NOT do the geometry
 *  push-out — that stays server-authoritative and tick-side, like the worm's
 *  surfaced ramp. */
export function stepDragonPiloted(pose, c, dtMs, bounds) {
    const dt = Math.min(0.05, Math.max(0, dtMs / 1000));
    const yaw = c.yaw;
    const pitch = Math.max(-0.9, Math.min(0.9, c.pitch));
    // yawPitchToDir, inlined (no per-input array alloc)
    const cp = Math.cos(pitch);
    const dx = -Math.sin(yaw) * cp, dy = Math.sin(pitch), dz = -Math.cos(yaw) * cp;
    const spd = 26 * (c.boost ? 1.7 : 1);
    let x = pose.x + dx * c.throttle * spd * dt;
    let y = pose.y + dy * c.throttle * spd * dt;
    let z = pose.z + dz * c.throttle * spd * dt;
    if (c.ascend)
        y += 14 * dt;
    if (c.descend)
        y -= 14 * dt;
    pose.x = Math.max(bounds.min[0] - 10, Math.min(bounds.max[0] + 10, x));
    pose.z = Math.max(bounds.min[2] - 10, Math.min(bounds.max[2] + 10, z));
    pose.y = Math.max(4, Math.min(bounds.max[1] + 60, y));
    pose.yaw = yaw;
    pose.pitch = pitch;
}
function collidesVehicle(world, k, half) {
    const a = {
        minX: k.x - half[0], maxX: k.x + half[0],
        minY: k.y, maxY: k.y + half[1] * 2,
        minZ: k.z - half[2], maxZ: k.z + half[2],
    };
    // Static geometry goes through the collider index (invariant 9): this is a
    // boolean any-hit query with the SAME strict `<`/`>` comparisons as the old
    // linear scan, so it is bit-identical (staticBoxOverlap's overlap path is
    // covered by agent/bench-rayhit.mjs; agent/bench-kart-collide.mjs adds a
    // direct A/B on this call). It matters because stepVehicle calls this MANY
    // times per driven-vehicle step — up to (substeps × 2 axes × curb-climb
    // lifts) — and it now runs on the client's driven-vehicle prediction too, so
    // a linear 962-box scan on medieval_castle was a real per-tick AND per-frame
    // cost every time someone drove.
    if (staticBoxOverlap(world, a.minX, a.minY, a.minZ, a.maxX, a.maxY, a.maxZ))
        return true;
    // moving platforms (train wagons, plane decks) are solid for vehicles too —
    // players collide with these; a kart/heli must not drive straight through
    for (const b of world.dynamic) {
        if (a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY && a.minZ < b.maxZ && a.maxZ > b.minZ) {
            return true;
        }
    }
    return false;
}
/** Carry a vehicle resting on a moving platform (UFO deck, train, plane) so
 *  a landed heli/kart rides along instead of the platform sliding out from
 *  under it. Same idea as the player's applyPlatformCarry — and, exactly like
 *  it, `scale` re-times the baked-in delta. The deltas in `world.dynamic` cover
 *  whatever interval the caller advanced its platform clock by: ONE server tick
 *  on the server, ONE RENDER FRAME on the client. The client replays one carry
 *  per pending input (a fixed 1/60 s of sim time each), so it must ask for
 *  `SIM_DT / frameDt` of the frame's delta or it silently integrates the deck
 *  at the monitor's refresh rate — rubber-banding a driven ride on a moving
 *  deck by (pending-count × frame-delta) every ack. Default 1 = "as baked",
 *  which the server and a live client frame both want. */
export function vehiclePlatformCarry(world, k, half, scale = 1) {
    for (const b of world.dynamic) {
        if (k.x + half[0] > b.minX && k.x - half[0] < b.maxX &&
            k.z + half[2] > b.minZ && k.z - half[2] < b.maxZ &&
            k.y >= b.maxY - 0.5 && k.y <= b.maxY + 0.9) {
            const rb = b;
            if (rb.dyaw !== undefined) {
                // scaled rigid-body carry: spin by a fraction of the yaw step about a
                // centre lerped the same fraction of the way (identity at 0, exact at 1)
                const cx = rb.rotCx ?? 0, cz = rb.rotCz ?? 0;
                const lx = k.x - cx, lz = k.z - cz;
                const c2 = Math.cos(rb.dyaw * scale), sn = Math.sin(rb.dyaw * scale);
                k.x = cx + ((rb.rotCx2 ?? 0) - cx) * scale + lx * c2 - lz * sn;
                k.z = cz + ((rb.rotCz2 ?? 0) - cz) * scale + lx * sn + lz * c2;
                k.y += b.dy * scale;
                k.yaw += rb.dyaw * scale; // the parked hull turns with the deck
            }
            else {
                k.x += b.dx * scale;
                k.z += b.dz * scale;
                k.y += b.dy * scale; // ride along (incl. altitude)
            }
            if (k.y < b.maxY) {
                k.y = b.maxY;
                if (k.vy < 0)
                    k.vy = 0;
            } // rising deck lifts it
            return true;
        }
    }
    return false;
}
/** Advance a vehicle one step. Mutates and returns k. */
export function stepVehicle(cfg, k, c, world, dtMs, killZ, carryScale = 1) {
    const dt = Math.min(0.05, Math.max(0.001, dtMs / 1000));
    const half = cfg.half;
    const sMul = cfg.speedMul ?? 1;
    const tMul = cfg.turnMul ?? 1;
    const fMul = cfg.frictionMul ?? 1;
    if (cfg.kind === "kart" && cfg.mkdd) {
        // ARENA bombkart only — Mario Kart: Double Dash!! Bob-omb Blast physics
        stepArenaKartDrive(cfg, k, c, world, dt);
    }
    else if (cfg.kind === "kart") {
        const comfortKart = cfg.accelMul !== undefined;
        // steering scales with forward speed
        const fwdSpeed = -(k.vx * -Math.sin(k.yaw) + k.vz * -Math.cos(k.yaw)); // NEGATED speed along heading (forward ⇒ negative)
        const drifting = !!c.drift && c.throttle > 0.15 && Math.abs(c.steer) > 0.18 && Math.abs(fwdSpeed) > 4;
        // MINI-TURBO (arena comfort kart only). Charge while drifting; on the frame
        // the drift breaks/releases, convert the charge to a boost tier; tick any
        // active boost down. Purely a function of (driftCharge, boostT, drifting,
        // dt) — both fields are replicated VehicleKin state so client prediction and
        // the server compute this identically (invariant 7). Non-comfort karts skip
        // this block entirely, so their fields stay 0 and nothing about them changes.
        let boosting = false;
        if (comfortKart) {
            const charge = k.driftCharge ?? 0;
            if (drifting) {
                k.driftCharge = Math.min(KART.mtChargeMax, charge + dt);
            }
            else {
                // On release, convert the charge to a boost tier. Each tier carries its
                // own duration AND strength (mtBoostMul1/2). Take the max of both against
                // any boost already running so a fresh tier can EXTEND or UPGRADE an
                // active boost but never downgrade its strength (matters only for the
                // rare drift→boost→drift→release overlap; keeps the fields monotone and
                // the re-seed/replay deterministic).
                let newT = 0, newMul = 0;
                if (charge >= KART.mtT2) {
                    newT = KART.mtBoost2;
                    newMul = KART.mtBoostMul2;
                }
                else if (charge >= KART.mtT1) {
                    newT = KART.mtBoost1;
                    newMul = KART.mtBoostMul1;
                }
                if (newT > 0) {
                    const wasBoosting = (k.boostT ?? 0) > 0;
                    k.boostT = Math.max(k.boostT ?? 0, newT);
                    k.boostMul = Math.max(wasBoosting ? (k.boostMul ?? 0) : 0, newMul);
                }
                k.driftCharge = 0;
            }
            let bt = k.boostT ?? 0;
            if (bt > 0) {
                boosting = true;
                bt = Math.max(0, bt - dt);
            }
            k.boostT = bt;
            // Clear the multiplier only on a step with NO active boost — NOT the final
            // boosting step (where bt has just reached 0 but `boosting` is still true
            // and fwdCap still needs boostMul). Zeroing it a step early dropped the cap
            // to the tier-1 fallback for one frame — a spurious speed dip at expiry.
            if (!boosting)
                k.boostMul = 0; // default 0, no replication churn when idle
        }
        // standstill fallback must match the moving sign: forward throttle ⇒ −1
        // (the old `|| c.throttle || 1` steered the WRONG way for the first ticks
        // when pulling away from rest, then flipped — a visible launch wiggle)
        const steerScale = comfortKart ? 0.78 : 1;
        const driftYawMul = comfortKart ? 1.08 : 1.42;
        k.yaw += c.steer * KART.turnRate * tMul * steerScale * (drifting ? driftYawMul : 1) * dt * Math.sign(fwdSpeed || -c.throttle || -1) * Math.min(1, Math.abs(fwdSpeed) / 7 + Math.abs(c.throttle) * 0.32);
        // accelerate along heading
        const fx = -Math.sin(k.yaw);
        const fz = -Math.cos(k.yaw);
        const rx = Math.cos(k.yaw);
        const rz = -Math.sin(k.yaw);
        const side = k.vx * rx + k.vz * rz;
        // boosting raises the forward top speed for the mini-turbo burst (arena only)
        const fwdCap = KART.maxSpeed * sMul * (boosting ? (k.boostMul || KART.mtBoostMul1) : 1);
        const targetSpeed = c.throttle > 0 ? fwdCap : c.throttle < 0 ? -KART.reverseSpeed : 0;
        const cur = k.vx * fx + k.vz * fz;
        let next = cur;
        if (c.throttle !== 0) {
            if (cfg.accelMul !== undefined) {
                // ARENA kart: exponential approach, but CRISP (τ≈0.4s). The old
                // τ≈1s "soft inertia" meant the kart's motion lagged a full second
                // behind the button — the classic visual/vestibular mismatch that
                // was making riders nauseous. Speed must track intent, not float.
                // While boosting, the approach is snappier (mtBoostRate) so the burst
                // reads as a KICK, not a slow drift up to the higher cap.
                const braking = cur !== 0 && Math.sign(targetSpeed || 1) !== Math.sign(cur);
                const rate = boosting ? KART.mtBoostRate : braking ? 3.2 : 2.4;
                next = cur + (targetSpeed - cur) * (1 - Math.exp(-rate * dt));
            }
            else {
                next = cur + Math.sign(targetSpeed - cur) * KART.accel * sMul * dt;
            }
        }
        // throttle released: the arena kart engine-brakes hard (τ≈0.55s) so
        // letting go actually means slowing — the old 3.5 m/s² coast kept the
        // world sliding for ~4s with no input, another nausea driver
        else
            next = cfg.accelMul !== undefined
                ? cur * Math.exp(-1.8 * dt)
                : cur - Math.sign(cur) * KART.friction * fMul * dt;
        if (c.throttle > 0) {
            // Non-comfort karts: the ORIGINAL hard clamp to base top speed, byte-for-
            // byte unchanged (fwdCap === base for them, boosting is always false).
            // Arena comfort kart just off a mini-turbo (next above base, not boosting):
            // bleed the overspeed smoothly instead of snapping down — a hard clamp
            // 33→22.8 m/s the instant the boost ends reads as hitting a wall.
            if (comfortKart && !boosting && next > fwdCap)
                next = Math.max(fwdCap, next - KART.mtOverspeedDecay * dt);
            else
                next = Math.min(next, fwdCap);
        }
        if (c.throttle < 0)
            next = Math.max(next, -KART.reverseSpeed);
        if (Math.abs(next) < 0.05 && c.throttle === 0)
            next = 0;
        // lateral grip: normal karts stick; drift keeps a sideways slide and feeds
        // a little extra slip outward, Mario-Kart style.
        const sideGrip = drifting ? Math.exp((comfortKart ? -3.0 : -2.2) * dt) : 0;
        const driftSlip = drifting ? c.steer * Math.min(comfortKart ? 4.2 : 7, Math.abs(next) * (comfortKart ? 0.22 : 0.34)) : 0;
        const nextSide = side * sideGrip + driftSlip * (1 - sideGrip);
        k.vx = fx * next + rx * nextSide;
        k.vz = fz * next + rz * nextSide;
        // jump jets (buggy): grounded ascend = a leap
        if (c.ascend && (cfg.jumpVel ?? 0) > 0 && Math.abs(k.vy) < 0.01)
            k.vy = cfg.jumpVel;
        // JUMP PADS launch karts too (arena: ride the beams; no fall damage for
        // vehicles, so big air is free fun). Shared code — the arena's client
        // prediction and the server agree tick-for-tick.
        if (Math.abs(k.vy) < 2 && world.pads.length > 0) {
            for (const pad of world.pads) {
                // `fling:0` pads are PURE-vertical player-lift shafts (Skyhaven's tight
                // violet columns), NOT arena launch beams — the player path treats them
                // as a gentle magnetic lift (movement.ts). A kart on a power:82 column
                // would be flung ~70 m/s straight up into the shaft ceiling; skip them.
                // Arena launch pads leave `fling` undefined, so they are unaffected.
                if (pad.fling === 0)
                    continue;
                const pdx = k.x - pad.x, pdz = k.z - pad.z;
                if (pdx * pdx + pdz * pdz < 2.2 * 2.2 && k.y >= pad.y - 0.6 && k.y <= pad.y + 1.8) {
                    k.vy = pad.power * (comfortKart ? 0.55 : 0.85); // arena comfort: big arcs made the chase-cam seasick
                    break;
                }
            }
        }
        // gravity. ARENA kart (comfortKart === bombkart === arena, the same proxy
        // every comfort tune above uses) falls under a STEEPER descent gravity than
        // it climbs — an asymmetric "MK hop": the rise is unchanged, so hop height
        // and pad/ramp peak are identical, but the DESCENT snaps back to the ground
        // instead of lingering in a slow lunar arc. The old symmetric 26 g made
        // every airborne beat float, which is what made the chase cam seasick (the
        // reduced pad power at the launch-pad branch above is a workaround for that
        // same float). Descent-only, stateless (pure function of vy's sign), so the
        // client's re-seed-then-replay kart prediction and the server tick compute
        // it identically — no new replicated state, no reconcile drift. Non-arena
        // karts keep the exact symmetric KART.gravity, byte-for-byte unchanged.
        k.vy -= (comfortKart && k.vy < 0 ? KART.arenaFallGravity : KART.gravity) * dt;
        k.roll = -c.steer * Math.min(1, Math.abs(fwdSpeed) / 14) * (drifting ? (comfortKart ? 0.18 : 0.42) : (comfortKart ? 0.11 : 0.25));
        k.pitch = 0;
    }
    else {
        // helicopter arcade flight (steer right = nose right)
        if (cfg.lookFlight)
            k.yaw = c.yaw;
        else
            k.yaw -= c.steer * HELI.turnRate * tMul * dt;
        const fx = -Math.sin(k.yaw);
        const fz = -Math.cos(k.yaw);
        const rx = Math.cos(k.yaw);
        const rz = -Math.sin(k.yaw);
        // forward/back thrust; lookFlight also strafes directly left/right
        const boost = c.boost ? 1.6 : 1; // L3/Ctrl afterburner
        k.vx += (fx * c.throttle + (cfg.lookFlight ? rx * c.steer : 0)) * HELI.moveAccel * sMul * boost * dt;
        k.vz += (fz * c.throttle + (cfg.lookFlight ? rz * c.steer : 0)) * HELI.moveAccel * sMul * boost * dt;
        // vertical
        if (cfg.plane) {
            // fixed-wing: no hover — idle = a slow forward glide that sinks, and
            // climbing shoves the plane FORWARD like a real climb-out
            if (c.ascend) {
                k.vy += HELI.lift * 0.9 * dt;
                k.vx += fx * HELI.moveAccel * 0.7 * sMul * dt;
                k.vz += fz * HELI.moveAccel * 0.7 * sMul * dt;
            }
            else if (c.descend) {
                k.vy -= HELI.lift * dt;
            }
            else {
                k.vy += (-2.4 - k.vy) * 1.6 * dt; // settle into a −2.4 m/s sink
                k.vx += fx * 3.5 * dt; // wings keep it drifting forward
                k.vz += fz * 3.5 * dt;
            }
        }
        else if (c.ascend)
            k.vy += HELI.lift * dt;
        else if (c.descend)
            k.vy -= HELI.lift * dt;
        else
            k.vy -= k.vy * 3 * dt; // HOVER by default — vertical velocity damps to 0
        k.vy = Math.max(-HELI.maxUp, Math.min(HELI.maxUp, k.vy));
        // horizontal drag
        k.vx -= k.vx * HELI.drag * dt;
        k.vz -= k.vz * HELI.drag * dt;
        const hspSq = k.vx * k.vx + k.vz * k.vz;
        if (hspSq > HELI.maxSpeed * HELI.maxSpeed) {
            const hsp = Math.sqrt(hspSq);
            k.vx *= HELI.maxSpeed / hsp;
            k.vz *= HELI.maxSpeed / hsp;
        }
        // visual banking
        k.pitch = -c.throttle * 0.22;
        k.roll = -c.steer * 0.3;
    }
    // integrate with axis-separated collision. Horizontal blocks try a step-up
    // (like the player) so karts climb curbs, road edges and terrain seams
    // instead of stopping dead on every little ledge.
    const VEHICLE_STEP = cfg.kind === "kart" ? 0.85 : 0.0;
    let wallDv = -1; // read only by the arena (mkdd) kart: biggest blocked axis speed (−1 = no contact)
    const tryHoriz = (axis, d) => {
        if (d === 0)
            return;
        const before = k[axis];
        k[axis] += d;
        if (!collidesVehicle(world, k, half))
            return;
        // blocked — try lifting up to VEHICLE_STEP and continuing (curb climb)
        if (VEHICLE_STEP > 0) {
            const savedY = k.y;
            // monster-truck karts probe much higher lifts, so they crest crates,
            // low walls and ledges in a single tick instead of stopping
            const lifts = cfg.climbAnything ? [VEHICLE_STEP, 1.7, 2.6, 3.4] : [VEHICLE_STEP];
            for (const lift of lifts) {
                k.y = savedY + lift;
                if (!collidesVehicle(world, k, half))
                    return; // stepped up onto the ledge
            }
            k.y = savedY;
        }
        k[axis] = before;
        wallDv = Math.max(wallDv, Math.abs(axis === "x" ? k.vx : k.vz));
        if (axis === "x")
            k.vx = 0;
        else
            k.vz = 0;
        // still blocked by something taller: a climb-anything kart scales the wall
        // — keep driving into it and it powers upward until it crests
        if (cfg.climbAnything)
            k.vy = Math.max(k.vy, 7.5);
    };
    const tryVert = (d) => {
        if (d === 0)
            return;
        const before = k.y;
        k.y += d;
        if (collidesVehicle(world, k, half)) {
            k.y = before;
            k.vy = 0;
        }
    };
    // SUBSTEP horizontal motion: a fast/drifting kart covers >1m per tick, and a
    // single endpoint check tunnels straight through thin planks/parapets
    {
        const mx = k.vx * dt, mz = k.vz * dt;
        const dist = Math.sqrt(mx * mx + mz * mz);
        const steps = Math.max(1, Math.ceil(dist / 0.3));
        for (let i = 0; i < steps; i++) {
            tryHoriz("x", mx / steps);
            tryHoriz("z", mz / steps);
        }
    }
    if (cfg.mkdd)
        arenaKartAfterMove(k, wallDv);
    // SUBSTEP vertical motion too: a pad-launched kart falls back at 30+ m/s
    // (1.5-2.5m per tick) — a single endpoint check skips clean through 0.3m
    // roof slabs and decks, landing karts INSIDE buildings
    {
        const my = k.vy * dt;
        const steps = Math.max(1, Math.ceil(Math.abs(my) / 0.6));
        for (let i = 0; i < steps && k.vy !== 0; i++)
            tryVert(my / steps);
    }
    // terminal fall clamp (mirrors the player's) so descent never outruns the substeps badly
    if (k.vy < -30)
        k.vy = -30;
    // hard play-space limits — vehicles use the TIGHT ring (you may FLY out of
    // the map with a jetpack/parachute, but never drive or heli out)
    if (k.x < world.groundMinX) {
        k.x = world.groundMinX;
        if (k.vx < 0)
            k.vx = 0;
    }
    if (k.x > world.groundMaxX) {
        k.x = world.groundMaxX;
        if (k.vx > 0)
            k.vx = 0;
    }
    if (k.z < world.groundMinZ) {
        k.z = world.groundMinZ;
        if (k.vz < 0)
            k.vz = 0;
    }
    if (k.z > world.groundMaxZ) {
        k.z = world.groundMaxZ;
        if (k.vz > 0)
            k.vz = 0;
    }
    if (k.y > world.ceilY) {
        k.y = world.ceilY;
        if (k.vy > 0)
            k.vy = 0;
    }
    // SUBMARINE stays IN the water: clamp the hull just under the surface (the
    // conning tower may breach) — it can dive to the seabed but never fly out.
    if (cfg.submarine && world.waters.length) {
        let surf = null;
        for (const w of world.waters) {
            if (k.x < w.minX || k.x > w.maxX || k.z < w.minZ || k.z > w.maxZ)
                continue;
            if (surf === null || w.maxY > surf)
                surf = w.maxY;
        }
        if (surf !== null) {
            // RISING TIDE: the swimmable surface rides up by world.tideY
            // (waterSurfaceAt in movement.ts does the same), so the hull cap must
            // too — otherwise on a tided map with subs (pirate_ship: amp 5.9 m) a
            // high tide pins the submarine metres BELOW the real sea surface, unable
            // to breach or ride the swell. tideY is 0 on every other map.
            const cap = surf + world.tideY + 0.4; // conning tower pokes ~0.4 m above the waterline
            if (k.y > cap) {
                k.y = cap;
                if (k.vy > 0)
                    k.vy = 0;
            }
        }
    }
    // ride any moving platform we're resting on (UFO deck / plane / train).
    // carryScale re-times the baked delta for the client's per-input replay (the
    // server passes 1 — its deltas are already one sim tick, applied once/tick).
    vehiclePlatformCarry(world, k, half, carryScale);
    // ground clamp for kart (rest on top of whatever it's on): if falling and now
    // colliding downward handled above; also clamp above killZ handled by caller.
    if (k.y < killZ - 2) {
        // fell out of the world — caller destroys it
    }
    return k;
}
/** re-export so the room can build a world without importing movement twice */
export { boxToAabb };
//# sourceMappingURL=entities.js.map